import React from 'react';
import { cn } from '../../utils/cn';

interface ProjectCardProps {
  project: {
    id: number;
    slug?: string;
    name_zh: string;
    name_vi: string;
    description_zh: string;
    description_vi: string;
    location_zh?: string;
    location_vi?: string;
    project_type?: string;
    end_date?: string;
    cover_image_url?: string;
    is_featured?: boolean;
    status?: string;
  };
  language: 'zh' | 'vi';
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, language }) => {
  // 获取分类标签
  const getCategoryLabel = () => {
    const categories: Record<string, { zh: string; vi: string; color: string }> = {
      electrical:    { zh: '电气工程',  vi: 'Điện công nghiệp',      color: 'bg-blue-100 text-blue-800' },
      hvac:          { zh: '暖通空调',  vi: 'HVAC',                   color: 'bg-green-100 text-green-800' },
      ventilation:   { zh: '通风系统',  vi: 'Thông gió',              color: 'bg-purple-100 text-purple-800' },
      decoration:    { zh: '建筑装饰',  vi: 'Trang trí kiến trúc',    color: 'bg-yellow-100 text-yellow-800' },
      waterproofing: { zh: '防水地坪',  vi: 'Chống thấm sàn',         color: 'bg-red-100 text-red-800' },
      renovation:    { zh: '改造工程',  vi: 'Cải tạo',                color: 'bg-indigo-100 text-indigo-800' },
    };

    const cat = categories[project.project_type || ''] || { zh: '其他', vi: 'Khác', color: 'bg-gray-100 text-gray-800' };
    return {
      label: language === 'zh' ? cat.zh : cat.vi,
      color: cat.color,
    };
  };

  const categoryInfo = getCategoryLabel();

  // 提取年份
  const year = project.end_date
    ? new Date(project.end_date).getFullYear()
    : null;

  const location = language === 'zh'
    ? (project.location_zh || '')
    : (project.location_vi || project.location_zh || '');

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
      {/* 项目图片 */}
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-blue-100 to-gray-100">
        {project.cover_image_url ? (
          <img
            src={project.cover_image_url}
            alt={language === 'zh' ? project.name_zh : project.name_vi}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-4xl mb-2 opacity-20">🏗️</div>
              <div className={cn('text-xs font-semibold px-2 py-1 rounded-full', categoryInfo.color)}>
                {categoryInfo.label}
              </div>
            </div>
          </div>
        )}

        {/* 年份标签 */}
        {year && (
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-700">
            {year}
          </div>
        )}

        {/* 精选标签 */}
        {project.is_featured && (
          <div className="absolute top-4 left-4 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
            {language === 'zh' ? '精选' : 'Nổi bật'}
          </div>
        )}
      </div>

      {/* 项目内容 */}
      <div className="p-6">
        {/* 分类标签 */}
        <div className="mb-3">
          <span className={cn('inline-flex items-center px-3 py-1 rounded-full text-xs font-medium', categoryInfo.color)}>
            {categoryInfo.label}
          </span>
        </div>

        {/* 项目标题 */}
        <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2">
          {language === 'zh' ? project.name_zh : project.name_vi}
        </h3>

        {/* 位置信息 */}
        {location && (
          <div className="flex items-center text-gray-600 mb-4">
            <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            <span className="text-sm">{location}</span>
          </div>
        )}

        {/* 项目描述 */}
        <p className="text-gray-600 mb-6 line-clamp-3">
          {language === 'zh' ? project.description_zh : project.description_vi}
        </p>

        {/* 查看详情按钮 */}
        <div className="w-full py-2 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-center">
          {language === 'zh' ? '查看详情' : 'Xem chi tiết'}
        </div>
      </div>
    </div>
  );
};

export { ProjectCard };
